/**
 * Route table (History API). Every top-level nav item resolves to a page —
 * upcoming sections render the phase placeholder, nothing 404s except a
 * genuinely unknown path.
 */
import { createBrowserRouter, Navigate, useLocation, useSearchParams } from "react-router";
import { legacyGrowthPath } from "../pages/growth/navigation.js";
import { lazy } from "react";
import { LoginPage } from "../auth/LoginPage.js";
import { ActivationPage } from "../auth/ActivationPage.js";
import { ForgotPasswordPage, ResetPasswordPage } from "../auth/PasswordRecoveryPage.js";
import { MfaPage } from "../auth/MfaPage.js";
import { ViewAsBoundary } from "../auth/ViewAsBoundary.js";
import {
  PortalLanding,
  RedirectIfAuthed,
  RequireAuth,
  RequireCapability,
  RequireMfa,
} from "../auth/guards.js";
import {
  AdminLayout,
  STYLEGUIDE_PATH,
  USERS_PATH,
} from "../pages/admin/AdminLayout.js";
import { DocumentsLayout, ConsolidatedDocumentsPage } from "../pages/documents/DocumentsLayout.js";
import { SUBMISSIONS_PATH, legacyAdminDocumentsPath } from "../pages/documents/paths.js";
import { legacyPracticeRecordsPath, practicesRedirectFromCrm } from "../pages/practices/paths.js";
import { practicesFromDentalGroups } from "../pages/crm-records/groupNavigation.js";
import {
  LINKS_PATH,
  SalesOrgLayout,
} from "../pages/sales-org/SalesOrgLayout.js";
import { AppShell } from "../shell/AppShell.js";
import { NotFoundPage } from "../shell/NotFoundPage.js";
import { PlaceholderPage } from "../shell/PlaceholderPage.js";

// Load workflow screens on demand; the shell/auth boundary stays immediately
// available. AppShell supplies one accessible loading state for these chunks.
const OrgImportPage = lazy(async () => ({
  default: (await import("../pages/admin/OrgImportPage.js")).OrgImportPage,
}));
const SalesRolesPage = lazy(async () => ({
  default: (await import("../pages/admin/SalesRolesPage.js")).SalesRolesPage,
}));
const UsersPage = lazy(async () => ({
  default: (await import("../pages/admin/UsersPage.js")).UsersPage,
}));
const DashboardPage = lazy(async () => ({
  default: (await import("../pages/dashboard/DashboardPage.js")).DashboardPage,
}));
const BundleDetailPage = lazy(async () => ({
  default: (await import("../pages/documents/BundleDetailPage.js"))
    .BundleDetailPage,
}));
const OnboardingPage = lazy(async () => ({
  default: (await import("../pages/documents/OnboardingPage.js"))
    .OnboardingPage,
}));
const SubmissionDetailPage = lazy(async () => ({
  default: (await import("../pages/documents/SubmissionDetailPage.js"))
    .SubmissionDetailPage,
}));
const SubmissionsPage = lazy(async () => ({
  default: (await import("../pages/documents/SubmissionsPage.js"))
    .SubmissionsPage,
}));
const PracticeDetailPage = lazy(async () => ({
  default: (await import("../pages/practices/PracticeDetailPage.js"))
    .PracticeDetailPage,
}));
const PracticesPage = lazy(async () => ({
  default: (await import("../pages/practices/PracticesPage.js")).PracticesPage,
}));
const LinkDetailPage = lazy(async () => ({
  default: (await import("../pages/sales-org/LinkDetailPage.js"))
    .LinkDetailPage,
}));
const LinksPage = lazy(async () => ({
  default: (await import("../pages/sales-org/LinksPage.js")).LinksPage,
}));
const OrganizationPage = lazy(async () => ({
  default: (await import("../pages/sales-org/OrganizationPage.js"))
    .OrganizationPage,
}));
const ProductsPage = lazy(async () => ({ default: (await import("../pages/products/ProductsPage.js")).ProductsPage }));
const ProductDetailPage = lazy(async () => ({ default: (await import("../pages/products/ProductDetailPage.js")).ProductDetailPage }));
const EnrollmentDetailPage = lazy(async () => ({ default: (await import("../pages/products/EnrollmentDetailPage.js")).EnrollmentDetailPage }));
const SearchPage = lazy(async () => ({ default: (await import("../practiceMaster/SearchPage.js")).SearchPage }));
const CustomFieldDefinitionsPage = lazy(async () => ({ default: (await import("../practiceMaster/CustomFieldDefinitionsPage.js")).CustomFieldDefinitionsPage }));
const LegacyLinkagePage = lazy(async () => ({ default: (await import("../practiceMaster/LegacyLinkagePage.js")).LegacyLinkagePage }));
const DisputesPage = lazy(async () => ({ default: (await import("../pages/attribution/DisputesPage.js")).DisputesPage }));
const IntegrationsPage = lazy(async () => ({ default: (await import("../pages/integrations/IntegrationsPage.js")).IntegrationsPage }));
const ApplicationsPage = lazy(async () => ({ default: (await import("../pages/integrations/ApplicationsPage.js")).ApplicationsPage }));
const CommissionsPage = lazy(async () => ({ default: (await import("../pages/commissions/CommissionsPage.js")).CommissionsPage }));
const RevenueSimulationPage = lazy(async () => ({ default: (await import("../pages/revenueSimulation/RevenueSimulationPage.js")).RevenueSimulationPage }));
const RevenuePage = lazy(async () => ({ default: (await import("../pages/revenue/RevenuePage.js")).RevenuePage }));

const PaymentsPage = lazy(async () => ({ default: (await import("../pages/payments/PaymentsPage.js")).PaymentsPage }));
const CrmPage = lazy(async () => ({ default: (await import("../pages/crm/CrmPage.js")).CrmPage }));
const CrmRecordsPage = lazy(async () => ({ default: (await import("../pages/crm-records/CrmRecordsPage.js")).CrmRecordsPage }));
const VideosPage = lazy(async () => ({ default: (await import("../pages/videos/VideosPage.js")).VideosPage }));
const FaqsPage = lazy(async () => ({ default: (await import("../pages/faqs/FaqsPage.js")).FaqsPage }));
const RepAgreementsPage = lazy(async () => ({ default: (await import("../pages/documents/RepAgreementsPage.js")).RepAgreementsPage }));
const CommunicationsPage = lazy(async () => ({ default: (await import("../pages/communications/CommunicationsPage.js")).CommunicationsPage }));
const BroadcastsPage = lazy(async () => ({ default: (await import("../pages/communications/broadcasts/BroadcastsPage.js")).BroadcastsPage }));
const DocumentUpdatesPage = lazy(async () => ({ default: (await import("../pages/document-updates/DocumentUpdatesPage.js")).DocumentUpdatesPage }));
const GettingStartedPage = lazy(async () => ({ default: (await import("../pages/reps/GettingStartedPage.js")).GettingStartedPage }));
const ExceptionsPage = lazy(async () => ({ default: (await import("../pages/exceptions/ExceptionsPage.js")).ExceptionsPage }));
const JoinPage = lazy(async () => ({ default: (await import("../pages/reps/JoinPage.js")).JoinPage }));
const RepApplicationsPage = lazy(async () => ({ default: (await import("../pages/reps/RepApplicationsPage.js")).RepApplicationsPage }));
const EmailActivityPage = lazy(async () => ({ default: (await import("../pages/admin/EmailActivityPage.js")).EmailActivityPage }));
const ZoomAttributionPage = lazy(async () => ({ default: (await import("../pages/admin/ZoomAttributionPage.js")).ZoomAttributionPage }));
const RepWorkspacePage = lazy(async () => ({ default: (await import("../pages/reps/RepWorkspacePage.js")).RepWorkspacePage }));
const RepDashboardPage = lazy(async () => ({ default: (await import("../pages/reps/RepDashboardPage.js")).RepDashboardPage }));

function LegacyDocumentRequirementsEntry() {
  const [params] = useSearchParams();
  return <Navigate to={legacyAdminDocumentsPath(params.toString())} replace />;
}
function LegacySigningEntry({ path }: { path: string }) {
  const [params] = useSearchParams();
  return <Navigate to={`${path}${params.size ? `?${params}` : ""}`} replace />;
}

function LegacyOrganizationEntry() {
  const [params] = useSearchParams();
  const next = new URLSearchParams(params);
  if (!next.has("person") && next.has("party")) next.set("person", next.get("party")!);
  next.delete("party");
  return <Navigate to={`/sales-organization${next.size ? `?${next}` : ""}`} replace />;
}

function CrmEntry() {
  const [params] = useSearchParams();
  // Preserve exact-Practice task/note links while moving registration bookmarks.
  return params.has("practice")
    ? <CrmPage />
    : <Navigate to={practicesRedirectFromCrm(params)} replace />;
}

function PracticesEntry() {
  const [params] = useSearchParams();
  const legacyDirectory = legacyPracticeRecordsPath(params);
  return legacyDirectory
    ? <Navigate to={legacyDirectory} replace />
    : <CrmRecordsPage view="practices" />;
}

function DentalGroupsEntry() {
  const [params] = useSearchParams();
  return <Navigate to={practicesFromDentalGroups(params)} replace />;
}

function LegacyGrowthEntry() {
  const [params] = useSearchParams();
  const { hash } = useLocation();
  return <Navigate to={legacyGrowthPath(params, hash)} replace />;
}

export function createAppRouter() {
  return createBrowserRouter([{ element: <ViewAsBoundary />, children: [
    {
      path: "/login",
      element: (
        <RedirectIfAuthed>
          <LoginPage />
        </RedirectIfAuthed>
      ),
    },
    { path: "/activate", element: <ActivationPage /> },
    { path: "/forgot-password", element: <ForgotPasswordPage /> },
    { path: "/reset-password", element: <ResetPasswordPage /> },
    { path: "/join", element: <JoinPage /> },
    { path: "/join/:referralAlias", element: <JoinPage /> },
    {
      element: <RequireAuth />,
      children: [
        { path: "/mfa", element: <MfaPage /> },
        {
          element: <RequireMfa />,
          children: [
            {
              element: <AppShell />,
              children: [
                {
                  index: true,
                  element: (
                    <PortalLanding>
                      <CrmRecordsPage view="dashboard" />
                    </PortalLanding>
                  ),
                },
                {
                  path: "practices",
                  element: <RequireCapability capability="practices:view" />,
                  children: [
                    { index: true, element: <PracticesEntry /> },
                    { path: "records", element: <PracticesPage /> },
                    { path: ":id", element: <PracticeDetailPage /> },
                  ],
                },
                { path: "crm", element: <RequireCapability capability="crm:work"><CrmEntry /></RequireCapability> },
                { path: "dental-groups", element: <RequireCapability capability="crm:work"><DentalGroupsEntry /></RequireCapability> },
                { path: "training/crm", element: <RequireCapability capability="crm:configure"><CrmPage /></RequireCapability> },
                { path: "training/signing", element: <RequireCapability capability="view:all"><DashboardPage /></RequireCapability> },
                { path: "rep-workspace", element: <RequireCapability capability="reps:read"><RepDashboardPage /></RequireCapability> },
                { path: "getting-started", element: <RequireCapability capability="reps:read"><GettingStartedPage /></RequireCapability> },
                { path: "faqs", element: <FaqsPage /> },
                { path: "rep-workspace/setup", element: <RequireCapability capability="reps:read"><RepWorkspacePage /></RequireCapability> },
                { path: "exceptions", element: <RequireCapability capability="exceptions:read"><ExceptionsPage /></RequireCapability> },
                {
                  path: "sales-organization",
                  element: (
                    <RequireCapability capability="org:view">
                      <SalesOrgLayout />
                    </RequireCapability>
                  ),
                  children: [
                    { index: true, element: <OrganizationPage /> },
                    { path: "performance", element: <RequireCapability capability="users:manage"><LegacyGrowthEntry /></RequireCapability> },
                    { path: "links", element: <LinksPage /> },
                    { path: "disputes", element: <RequireCapability capability="disputes:create"><DisputesPage /></RequireCapability> },
                    {
                      path: "links/:id",
                      element: (
                        <RequireCapability
                          capability="view:all"
                          fallbackTo={LINKS_PATH}
                        >
                          <LinkDetailPage />
                        </RequireCapability>
                      ),
                    },
                  ],
                },
                {
                  path: "products",
                  element: <RequireCapability capability="products:view" />,
                  children: [
                    { index: true, element: <ProductsPage /> },
                    { path: ":id", element: <ProductDetailPage /> },
                  ],
                },
                { path: "enrollments/:id", element: <RequireCapability capability="products:view"><EnrollmentDetailPage /></RequireCapability> },
                { path: "search", element: <RequireCapability capability="practices:view"><SearchPage /></RequireCapability> },
                {
                  path: "commissions",
                  element: <RequireCapability capability="commissions:manage"><CommissionsPage /></RequireCapability>,
                },
                {
                  path: "revenue-simulation",
                  element: <RequireCapability capability="commissions:manage"><RevenueSimulationPage /></RequireCapability>,
                },
                {
                  path: "revenue",
                  element: <RequireCapability capability="commissions:manage"><RevenuePage /></RequireCapability>,
                },
                {
                  path: "payments",
                  element: <RequireCapability capability="payments:manage"><PaymentsPage /></RequireCapability>,
                },
                {
                  path: "communications",
                  element: <RequireCapability capability="communications:read"><CommunicationsPage /></RequireCapability>,
                },
                { path: "communications/broadcasts", element: <RequireCapability capability="users:manage"><BroadcastsPage /></RequireCapability> },
                { path: "document-updates", element: <DocumentUpdatesPage /> },
                { path: "document-updates/admin", element: <RequireCapability capability="users:manage"><LegacyDocumentRequirementsEntry /></RequireCapability> },
                {
                  path: "documents",
                  element: <DocumentsLayout />,
                  children: [
                    {
                      index: true,
                      element: <RequireCapability capability="documents:read"><ConsolidatedDocumentsPage /></RequireCapability>,
                    },
                    { path: "submissions", element: <RequireCapability capability="view:all"><SubmissionsPage /></RequireCapability> },
                    {
                      path: "submissions/:id",
                      element: <RequireCapability capability="view:all"><SubmissionDetailPage /></RequireCapability>,
                    },
                    { path: "onboarding", element: <RequireCapability capability="view:all"><OnboardingPage /></RequireCapability> },
                    { path: "onboarding/:id", element: <RequireCapability capability="view:all"><BundleDetailPage /></RequireCapability> },
                    { path: "rep-agreements", element: <RequireCapability capability="view:all"><RepAgreementsPage /></RequireCapability> },
                  ],
                },
                {
                  path: "videos",
                  element: <VideosPage />,
                },
                {
                  path: "integrations",
                  element: <RequireCapability capability="integrations:manage"><IntegrationsPage /></RequireCapability>,
                },
                { path: "applications", element: <RequireCapability capability="integrations:manage"><ApplicationsPage /></RequireCapability> },
                {
                  path: "reports",
                  element: <PlaceholderPage navId="reports" />,
                },
                {
                  path: "admin",
                  element: (
                    <RequireCapability capability="users:manage">
                      <AdminLayout />
                    </RequireCapability>
                  ),
                  children: [
                    {
                      index: true,
                      element: <Navigate to={USERS_PATH} replace />,
                    },
                    { path: "users", element: <UsersPage /> },
                    { path: "rep-applications", element: <RepApplicationsPage /> },
                    { path: "email-activity", element: <EmailActivityPage /> },
                    { path: "zoom-attribution", element: <ZoomAttributionPage /> },
                    { path: "sales-roles", element: <SalesRolesPage /> },
                    { path: "org-import", element: <OrgImportPage /> },
                    { path: "custom-fields", element: <CustomFieldDefinitionsPage /> },
                    { path: "practice-linkage", element: <LegacyLinkagePage /> },
                    {
                      // Demo-heavy; split out of the main bundle.
                      path: "styleguide",
                      lazy: async () => ({
                        Component: (
                          await import("../pages/admin/StyleGuidePage.js")
                        ).StyleGuidePage,
                      }),
                    },
                  ],
                },
                { path: "organization", element: <LegacyOrganizationEntry /> },
                // Legacy hash-router destinations, kept as redirects for bookmarks.
                {
                  path: "submissions",
                  element: <LegacySigningEntry path={SUBMISSIONS_PATH} />,
                },
                {
                  path: "bundles",
                  element: <LegacySigningEntry path="/documents/onboarding" />,
                },
                {
                  path: "links",
                  element: <Navigate to="/sales-organization/links" replace />,
                },
                {
                  path: "users",
                  element: <Navigate to={USERS_PATH} replace />,
                },
                {
                  path: "styleguide",
                  element: <Navigate to={STYLEGUIDE_PATH} replace />,
                },
                { path: "*", element: <NotFoundPage /> },
              ],
            },
          ],
        },
      ],
    },
  ] }]);
}
